import { useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import queryString from 'query-string';

import { checkIsBase64 } from 'helpers/base64';
import { isPrelandPage } from 'helpers/route';
import { getUserIdFromCookies } from 'helpers/userId';
import { useRegistrationAnalytics } from 'hooks/analytics/useRegistrationAnalytics';
import { useAutoRegisterMutation } from 'hooks/mutations/useAutoRegisterMutation';
import { useSystemSearchQueries } from 'hooks/useSystemSearchQueries';
import { setAuthenticated } from 'store/auth/authSlice';

const AUTO_REGISTRATION_FAIL_REDIRECT_LANDING_NUMBER = 12;

export const useAuthenticateUser = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const { getAutoRegisterQuery } = useSystemSearchQueries();
  const { trackRegistrationSuccess } = useRegistrationAnalytics();

  const autoRegistrationMutation = useAutoRegisterMutation();

  const { key: autoRegisterQueryKey, value: autoRegisterQueryValue } =
    getAutoRegisterQuery();
  const { [autoRegisterQueryKey]: autoRegisterQuery, ...restQueries } =
    queryString.parse(location.search);

  const handleAutoRegistrationSuccess = () => {
    dispatch(setAuthenticated(true));
    trackRegistrationSuccess();

    history.replace(
      `${location.pathname}?${queryString.stringify(restQueries)}`
    );
  };

  const handleAutoRegistrationFail = (error: any) => {
    dispatch(setAuthenticated(false));

    if (error?.response?.status === 422 && !isPrelandPage(location.pathname)) {
      history.replace(
        `/landing/lp${AUTO_REGISTRATION_FAIL_REDIRECT_LANDING_NUMBER}?${queryString.stringify(
          restQueries
        )}`
      );
    }
  };

  const tryAuthenticateUser = () => {
    if (autoRegisterQueryValue) {
      const queries = queryString.parse(location.search);

      const email = checkIsBase64(String(queries.email))
        ? atob(String(queries.email))
        : queries.email;

      autoRegistrationMutation.mutate(
        {
          ...queries,
          email: email || undefined,
          history_length: history.length > 1 ? String(history.length) : null,
        },
        {
          onSuccess: handleAutoRegistrationSuccess,
          onError: handleAutoRegistrationFail,
        }
      );
    } else {
      dispatch(setAuthenticated(Boolean(getUserIdFromCookies())));
    }
  };

  return {
    tryAuthenticateUser,
  };
};
